import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { JobStatus } from "@hightouch/lib/job-queue/job-status";
import type { DisclaimerSpec } from "@hightouch/lib/ad-studio/disclaimer/types";
import {
  useBundleVideoExportsMutation,
  useExportBulkVideoAdsMutation,
  useGetBulkVideoExportStatusesQuery,
  useGetVideoExportBundleStatusQuery,
  type GetBulkVideoExportStatusesQuery,
  type GetVideoExportBundleStatusQuery,
} from "src/graphql";
import { useGovernedRefetchInterval } from "src/hooks/use-governed-refetch-interval";
import { captureException } from "src/lib/monitoring";
import { downloadFromUrl } from "src/utils/download";
import type { VideoExportTarget } from "./bulk-video-download-targets";

const RENDER_POLL_INTERVAL_MS = 3_000;
const BUNDLE_POLL_INTERVAL_MS = 2_000;
// Renders routinely take minutes, so poll at the base interval for 10 minutes
// before backing off rather than the 2 minutes a docx export needs.
const RENDER_POLLS_BEFORE_BACKOFF = (10 * 60_000) / RENDER_POLL_INTERVAL_MS;
const BUNDLE_POLLS_BEFORE_BACKOFF = (2 * 60_000) / BUNDLE_POLL_INTERVAL_MS;
/** The client default is no retries; one blip must not end a flow mid-wait. */
const POLL_RETRIES = 3;

/** Terminal render states, mirroring `video_exports.status`. */
const RENDER_COMPLETE = "complete";
const RENDER_FAILED = "failed";

const TERMINAL_BUNDLE_STATUSES: ReadonlySet<string> = new Set([
  JobStatus.Success,
  JobStatus.Failed,
  JobStatus.Cancelled,
]);

export type BulkVideoDownloadPhase =
  | "idle"
  | "rendering"
  | "bundling"
  | "done"
  | "error";

export type BulkVideoDownloadItem = {
  target: VideoExportTarget;
  videoExportId: string | null;
  status: "pending" | "rendering" | "complete" | "failed";
};

export type BulkVideoDownloadState = {
  phase: BulkVideoDownloadPhase;
  items: BulkVideoDownloadItem[];
  completeCount: number;
  failedCount: number;
  /** Set when the whole flow fails, as opposed to individual ads failing. */
  error: string | null;
  /** Ads that rendered but couldn't be read when the archive was built. */
  skippedCount: number;
};

type BundleStatus =
  GetVideoExportBundleStatusQuery["getVideoExportBundleStatus"];

/** `reason` values the bundle job sets on its NonRetriableJobError meta. */
const BUNDLE_FAILURE_MESSAGES: Record<string, string> = {
  too_many_entries:
    "Too many videos were selected for one download. Select fewer ads and try again.",
  no_available_renders: "None of the selected videos finished rendering.",
};

// A GraphQL or network message names endpoints and ids, so every failure path
// below sends the detail to Sentry and shows the user a fixed string instead.
function bundleFailureMessage(reason: string | null | undefined): string {
  return (
    (reason ? BUNDLE_FAILURE_MESSAGES[reason] : undefined) ??
    "Couldn't prepare the download. Try again."
  );
}

/**
 * Drives a bulk video download end to end: fan out renders, watch them finish,
 * ask the API to archive the finished ones, then hand the browser the URL for
 * the zip.
 *
 * The two polling stages are separate queries rather than one state machine
 * because they poll different things at different cadences — renders take
 * minutes, archiving takes seconds.
 */
export function useBulkVideoDownload(args: {
  targets: VideoExportTarget[];
  /** Base name for the archive, without an extension. */
  filename: string;
  /** One AI-disclosure choice applied to every video in the batch. */
  disclaimerSpec?: DisclaimerSpec;
}) {
  const { targets, filename, disclaimerSpec } = args;

  const [items, setItems] = useState<BulkVideoDownloadItem[]>([]);
  const [phase, setPhase] = useState<BulkVideoDownloadPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [bundleJobId, setBundleJobId] = useState<string | null>(null);
  const [skippedCount, setSkippedCount] = useState(0);
  // Latches so the one-shot transitions (enqueue renders, request the archive,
  // trigger the download) can't fire twice when a poll re-renders the hook.
  const startedRef = useRef(false);
  const bundleRequestedRef = useRef(false);
  const downloadedRef = useRef(false);

  // Held in refs, not read directly in the effects: react-query hands back a
  // new mutation object on every render, so listing one as a dependency would
  // re-run the effect every render.
  const exportMutation = useExportBulkVideoAdsMutation();
  const exportMutationRef = useRef(exportMutation);
  exportMutationRef.current = exportMutation;
  const bundleMutation = useBundleVideoExportsMutation();
  const bundleMutationRef = useRef(bundleMutation);
  bundleMutationRef.current = bundleMutation;
  // Same reasoning: the spec is fixed for the life of this hook, and keeping it
  // out of the start effect's deps stops an inline object from re-running it.
  const specRef = useRef(disclaimerSpec);
  specRef.current = disclaimerSpec;

  const { refetchInterval: renderPollInterval, resetBackoff: resetRenderPoll } =
    useGovernedRefetchInterval<GetBulkVideoExportStatusesQuery>({
      baseMs: RENDER_POLL_INTERVAL_MS,
      unchangedPollsBeforeBackoff: RENDER_POLLS_BEFORE_BACKOFF,
      // Back off only when no render has changed state — completedAt and other
      // always-fresh fields would otherwise defeat it.
      compareKey: (data) =>
        data?.getBulkVideoExportStatuses?.statuses
          ?.map((s) => `${s.videoExportId}:${s.status}`)
          .join(",") ?? "",
    });

  const { refetchInterval: bundlePollInterval, resetBackoff: resetBundlePoll } =
    useGovernedRefetchInterval<GetVideoExportBundleStatusQuery>({
      baseMs: BUNDLE_POLL_INTERVAL_MS,
      unchangedPollsBeforeBackoff: BUNDLE_POLLS_BEFORE_BACKOFF,
      compareKey: (data) => data?.getVideoExportBundleStatus?.status,
    });

  // Kicks off on mount. The caller mounts this hook only while the download is
  // open, so there is no "inactive" state to reset back to — closing unmounts
  // and the state goes with it. Layout effect, not a plain one: an effect that
  // runs after paint leaves one frame where the phase still reads "idle", and
  // the dialog paints that as a finished export the user can safely close.
  useLayoutEffect(() => {
    if (startedRef.current || targets.length === 0) return;
    startedRef.current = true;

    setPhase("rendering");
    setItems(
      targets.map((target) => ({
        target,
        videoExportId: null,
        status: "pending" as const,
      })),
    );

    void (async () => {
      try {
        const response = await exportMutationRef.current.mutateAsync({
          input: {
            targets: targets.map((target) => ({
              adBriefAssetVersionId: target.adBriefAssetVersionId,
            })),
            ...(specRef.current ? { disclaimerSpec: specRef.current } : {}),
          },
        });

        // The response echoes each target back, so pair on its version rather than trusting array order.
        const exportIdByKey = new Map(
          response.exportBulkVideoAds.exports.map((e) => [
            e.adBriefAssetVersionId,
            e.videoExportId,
          ]),
        );
        setItems((prev) =>
          prev.map((item) => {
            const videoExportId = exportIdByKey.get(item.target.key) ?? null;
            // A target the server didn't echo has no render to wait on.
            return {
              ...item,
              videoExportId,
              status:
                videoExportId === null
                  ? ("failed" as const)
                  : ("rendering" as const),
            };
          }),
        );
        resetRenderPoll();
      } catch (err) {
        captureException(err);
        setError("Couldn't start the renders. Try again.");
        setPhase("error");
      }
    })();
  }, [targets, resetRenderPoll]);

  const videoExportIds = items
    .map((item) => item.videoExportId)
    .filter((id): id is string => id !== null);

  const { data: renderStatusData, error: renderStatusError } =
    useGetBulkVideoExportStatusesQuery(
      { input: { videoExportIds } },
      {
        enabled: phase === "rendering" && videoExportIds.length > 0,
        refetchInterval: renderPollInterval,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        retry: POLL_RETRIES,
      },
    );

  useEffect(() => {
    if (phase !== "rendering" || !renderStatusError) return;
    captureException(renderStatusError);
    setError("Couldn't check on the renders. Try again.");
    setPhase("error");
  }, [phase, renderStatusError]);

  // Fold poll results back onto the items so the dialog can show per-ad state.
  useEffect(() => {
    const statuses = renderStatusData?.getBulkVideoExportStatuses?.statuses;
    if (!statuses) return;
    const statusById = new Map(
      statuses.map((s) => [s.videoExportId, s.status]),
    );
    setItems((prev) => {
      let changed = false;
      const next = prev.map((item) => {
        if (!item.videoExportId) return item;
        // Terminal stays terminal: a lagging replica that omits the id on a
        // later poll must not rewind a settled row and stall bundling.
        if (item.status === "complete" || item.status === "failed") return item;
        const reported = statusById.get(item.videoExportId);
        // An omitted id can be a lagging read replica rather than a deleted
        // export, so keep waiting rather than calling the render failed.
        const status =
          reported === RENDER_FAILED
            ? ("failed" as const)
            : reported === RENDER_COMPLETE
              ? ("complete" as const)
              : ("rendering" as const);
        if (status === item.status) return item;
        changed = true;
        return { ...item, status };
      });
      // Returning the previous array lets React bail out of the re-render, so a
      // ten-minute wait doesn't re-render the list on every poll.
      return changed ? next : prev;
    });
  }, [renderStatusData]);

  const completeCount = items.filter((i) => i.status === "complete").length;
  const failedCount = items.filter((i) => i.status === "failed").length;
  const allSettled =
    items.length > 0 && completeCount + failedCount === items.length;

  // Once every render has settled, archive whatever succeeded.
  useEffect(() => {
    if (phase !== "rendering" || !allSettled || bundleRequestedRef.current) {
      return;
    }
    bundleRequestedRef.current = true;

    const completedIds = items.flatMap((i) =>
      i.status === "complete" && i.videoExportId ? [i.videoExportId] : [],
    );

    if (completedIds.length === 0) {
      setError(bundleFailureMessage("no_available_renders"));
      setPhase("error");
      return;
    }

    setPhase("bundling");
    void (async () => {
      try {
        const response = await bundleMutationRef.current.mutateAsync({
          input: {
            videoExportIds: completedIds,
            // Stamped here rather than by the caller: the name has to be fixed
            // once per request, and callers recompute it on every render.
            filename: `${filename}-${Date.now()}`,
          },
        });
        resetBundlePoll();
        setBundleJobId(response.bundleVideoExports.jobId);
      } catch (err) {
        captureException(err);
        setError("Couldn't prepare the download. Try again.");
        setPhase("error");
      }
    })();
  }, [phase, allSettled, items, filename, resetBundlePoll]);

  const { data: bundleStatusData, error: bundleError } =
    useGetVideoExportBundleStatusQuery(
      { input: { jobId: bundleJobId ?? "" } },
      {
        enabled: Boolean(bundleJobId) && phase === "bundling",
        refetchInterval: (latest, query) => {
          const status = latest?.getVideoExportBundleStatus?.status;
          if (status && TERMINAL_BUNDLE_STATUSES.has(status)) {
            return false;
          }
          return bundlePollInterval(latest, query);
        },
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        retry: POLL_RETRIES,
      },
    );

  // One effect for both terminal data and fetch errors so the paths can't
  // double-fire; an error on the latest poll invalidates stale data.
  useEffect(() => {
    if (phase !== "bundling") return;

    if (bundleError) {
      captureException(bundleError);
      setError("Couldn't check on the download. Try again.");
      setPhase("error");
      return;
    }
    const bundleStatus: BundleStatus | undefined =
      bundleStatusData?.getVideoExportBundleStatus;
    if (!bundleStatus) return;

    if (bundleStatus.status === JobStatus.Success) {
      // The caller builds a link from these, so a body missing either would
      // produce `undefined` in an href.
      if (!bundleStatus.downloadUrl || !bundleStatus.filename) {
        captureException(
          new Error("Video export bundle finished without a download URL"),
        );
        setError("The download is ready but couldn't be started");
        setPhase("error");
        return;
      }
      if (!downloadedRef.current) {
        downloadedRef.current = true;
        downloadFromUrl(bundleStatus.downloadUrl, bundleStatus.filename);
      }
      setSkippedCount(bundleStatus.skippedCount ?? 0);
      setPhase("done");
    } else if (bundleStatus.status === JobStatus.Failed) {
      // The worker's message is diagnostic, not copy: an S3 or network failure
      // names keys and endpoints, so it goes to Sentry and the reason code
      // picks what the user reads.
      captureException(
        new Error(
          `Video export bundle failed (${bundleStatus.reason ?? "unknown"}): ${bundleStatus.error}`,
        ),
      );
      setError(bundleFailureMessage(bundleStatus.reason));
      setPhase("error");
    } else if (bundleStatus.status === JobStatus.Cancelled) {
      setError("The download was canceled");
      setPhase("error");
    }
  }, [phase, bundleStatusData, bundleError]);

  const state: BulkVideoDownloadState = {
    phase,
    items,
    completeCount,
    failedCount,
    error,
    skippedCount,
  };

  return state;
}
